import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Play, ExternalLink, Music2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

const INITIAL_DISPLAY_COUNT = 6;
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const BANNER_IMAGE = 'https://customer-assets.emergentagent.com/job_awaaz-portfolio/artifacts/cd8qjwuh_Gemini_Generated_Image_4381ql4381ql4381.png';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

const AlbumCard = ({ album, index }) => (
  <Card
    className="bg-[#1e2749]/50 border-[#2d3561] hover:border-[#d4af37] transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group overflow-hidden"
    style={{
      animationDelay: `${index * 100}ms`,
      animation: 'fadeInUp 0.6s ease-out forwards',
      opacity: 0
    }}
  >
    <div className="relative overflow-hidden">
      <img
        src={resolveUrl(album.coverUrl)}
        alt={album.title}
        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1128] to-transparent opacity-60" />
      <div className="absolute top-4 right-4">
        <span className="bg-[#d4af37] text-[#0a1128] px-3 py-1 rounded-full text-sm font-semibold">
          {album.type}
        </span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Button className="bg-[#d4af37] text-[#0a1128] hover:bg-[#c5a572] rounded-full p-4">
          <Play className="w-8 h-8" />
        </Button>
      </div>
    </div>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
            {album.title}
          </h3>
          {album.subtitle && (
            <p className="text-sm text-[#d4af37] mb-2">{album.subtitle}</p>
          )}
        </div>
        <span className="text-gray-400 text-sm">{album.year}</span>
      </div>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{album.description}</p>
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <span className="flex items-center">
          <Music2 className="w-4 h-4 mr-1" />
          {album.tracks} {album.tracks === 1 ? 'track' : 'tracks'}
        </span>
        <span>{album.duration}</span>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          className="w-full border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300"
          onClick={() => window.open(album.streamUrl, '_blank')}
          data-testid={`spotify-btn-${index}`}
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Listen on Spotify
        </Button>
        {album.youtubeUrl && (
          <Button
            variant="outline"
            className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300"
            onClick={() => window.open(album.youtubeUrl, '_blank')}
            data-testid={`youtube-btn-${index}`}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Listen on YouTube
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

const SignatureBanner = ({ totalReleases }) => (
  <div className="relative mb-16 rounded-2xl overflow-hidden group" data-testid="signature-banner">
    <div className="relative h-64 md:h-80">
      <img
        src={BANNER_IMAGE}
        alt="Awaaz-e-Ishq Collection"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1128]/90 via-[#0a1128]/50 to-transparent" />
      <div className="absolute inset-0 flex items-center px-8 md:px-16">
        <div>
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            The Complete Collection
          </p>
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Awaaz-e-Ishq
          </h3>
          <p className="text-gray-300 text-sm md:text-base max-w-md leading-relaxed">
            An exploration of love through poetic Urdu lyrics and soulful melodies &mdash; {totalReleases} original compositions
          </p>
          <div className="mt-4 w-16 h-0.5 bg-[#d4af37]" />
        </div>
      </div>
    </div>
  </div>
);

const Discography = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const fetchReleases = useCallback(async () => {
    try {
      const response = await api.getReleases();
      setReleases(response.data);
    } catch (err) {
      console.error('Error fetching releases:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const displayedReleases = showAll ? releases : releases.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = releases.length > INITIAL_DISPLAY_COUNT;

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-[#0a1128] via-[#1e2749] to-[#0a1128]">
        <div className="container mx-auto px-6 text-center">
          <div className="text-white text-xl">Loading releases...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="discography" className="py-20 bg-gradient-to-b from-[#0a1128] via-[#1e2749] to-[#0a1128]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Discography
          </h2>
          <div className="w-24 h-1 bg-[#d4af37] mx-auto mb-4" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            A collection of stories, emotions, and melodies crafted with passion
          </p>
        </div>

        <SignatureBanner totalReleases={releases.length} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedReleases.map((album, index) => (
            <AlbumCard key={album._id} album={album} index={index} />
          ))}
        </div>

        {hasMore && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a1128] transition-all duration-300 px-8 py-3 text-base"
              onClick={() => setShowAll(!showAll)}
              data-testid="show-more-btn"
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-5 h-5 mr-2" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-5 h-5 mr-2" />
                  View All Releases ({releases.length})
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Discography;
